import type { Database } from '@/integrations/supabase/types';
import {
  BAY_WIDTH, BAY_HEIGHT, PARKING_WIDTH, PARKING_HEIGHT,
} from '@/components/facility/constants';

// ─── DB row alias ─────────────────────────────────────
export type ShopBayRow = Database['public']['Tables']['shop_bays']['Row'];
export type ParkingLotSpotRow = Database['public']['Tables']['parking_lot_spots']['Row'];
type BayTypeEnum = Database['public']['Enums']['bay_type'];

// ─── Facility entity types ────────────────────────────
export type FacilityEntityType =
  | 'lift_2post'
  | 'lift_4post'
  | 'lift_scissor'
  | 'lift_single_post'
  | 'alignment'
  | 'adas'
  | 'alignment_adas'
  | 'wash'
  | 'inspection'
  | 'flat'
  | 'parking_space'
  | 'zone'
  | 'door';

// ─── Rendering config per type ────────────────────────
export interface EntityRenderConfig {
  color: string;         // hsl semantic token name
  icon: string;          // lucide icon name
  displayLabel: string;
  defaultWidth: number;
  defaultHeight: number;
}

// Default footprints (new entities only — persisted geometry is never
// resized on load). Standard vehicle service bays share the canonical
// BAY_WIDTH×BAY_HEIGHT (≈14×24 ft) footprint: all four lift types, plus
// wash, inspection and flat bays, which are ordinary one-vehicle work bays.
// Alignment, ADAS and Alignment+ADAS keep their own wider specialty
// envelopes (room for alignment heads / ADAS calibration targets around the
// vehicle); that specialty sizing is intentionally out of scope here.
// Zones and doors are non-vehicle markers with their own sizes.
export const ENTITY_RENDER_CONFIG: Record<FacilityEntityType, EntityRenderConfig> = {
  lift_2post:       { color: 'hsl(var(--primary))',   icon: 'ArrowUpFromLine',  displayLabel: '2-Post Lift',       defaultWidth: BAY_WIDTH, defaultHeight: BAY_HEIGHT },
  lift_4post:       { color: 'hsl(210 60% 50%)',      icon: 'Columns3',         displayLabel: '4-Post Lift',       defaultWidth: BAY_WIDTH, defaultHeight: BAY_HEIGHT },
  lift_scissor:     { color: 'hsl(260 50% 55%)',      icon: 'Scissors',         displayLabel: 'Scissor Lift',      defaultWidth: BAY_WIDTH, defaultHeight: BAY_HEIGHT },
  lift_single_post: { color: 'hsl(180 50% 45%)',      icon: 'ArrowUp',          displayLabel: 'Single-Post Lift',  defaultWidth: BAY_WIDTH, defaultHeight: BAY_HEIGHT },
  alignment:        { color: 'hsl(150 60% 40%)',      icon: 'Crosshair',        displayLabel: 'Alignment',         defaultWidth: 340,       defaultHeight: 360 },
  adas:             { color: 'hsl(30 80% 50%)',       icon: 'ScanLine',         displayLabel: 'ADAS',              defaultWidth: 340,       defaultHeight: 360 },
  alignment_adas:   { color: 'hsl(45 80% 45%)',       icon: 'Target',           displayLabel: 'Alignment + ADAS',  defaultWidth: 360,       defaultHeight: 380 },
  wash:             { color: 'hsl(200 70% 55%)',      icon: 'Droplets',         displayLabel: 'Wash Bay',          defaultWidth: BAY_WIDTH, defaultHeight: BAY_HEIGHT },
  inspection:       { color: 'hsl(280 50% 50%)',      icon: 'ClipboardCheck',   displayLabel: 'Inspection',        defaultWidth: BAY_WIDTH, defaultHeight: BAY_HEIGHT },
  flat:             { color: 'hsl(0 0% 50%)',         icon: 'Square',           displayLabel: 'Flat Bay',          defaultWidth: BAY_WIDTH, defaultHeight: BAY_HEIGHT },
  parking_space:    { color: 'hsl(220 15% 60%)',      icon: 'Car',              displayLabel: 'Parking',           defaultWidth: PARKING_WIDTH, defaultHeight: PARKING_HEIGHT },
  zone:             { color: 'hsl(0 0% 70%)',         icon: 'LayoutGrid',       displayLabel: 'Zone',              defaultWidth: 400,       defaultHeight: 300 },
  door:             { color: 'hsl(25 60% 45%)',       icon: 'DoorOpen',         displayLabel: 'Door',              defaultWidth: 100,       defaultHeight: 40 },
};

// ─── Facility entity (render model) ───────────────────
/**
 * Which table owns an entity's persistence. Set explicitly at construction
 * and never inferred later (from label, entityType or a query result):
 *   - 'shop_bay'         — a shop_bays row. Includes FacilityEditor-created
 *                          parking, which the DB mirrors one-way into
 *                          parking_lot_spots under the same id.
 *   - 'parking_lot_spot' — a standalone parking_lot_spots row with no
 *                          shop_bays counterpart (e.g. onboarding parking).
 *                          Saved by UPDATE on parking_lot_spots by its
 *                          existing id; never inserted into shop_bays.
 */
export type FacilityStorageSource = 'shop_bay' | 'parking_lot_spot';

export interface FacilityEntity {
  id: string;
  storageSource: FacilityStorageSource;
  /** parking_lot_spots.layout_id — only for storageSource 'parking_lot_spot' */
  parkingLayoutId?: string | null;
  entityType: FacilityEntityType;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  isOutdoor: boolean;
  isActive: boolean;
  isLockedOut: boolean;
  lockoutReason: string | null;
  isCommunity: boolean;
  assignedTechId: string | null;
  teamId: string | null;
  shopId: string;
  bayNumber: number | null;
  /** Original DB bay_type enum value for persistence */
  dbBayType: BayTypeEnum;
  dbBayCategory: string;
  /** Optional group id – entities in the same group move together */
  groupId?: string | null;
  /** Lift metadata (only relevant for lift bay types) */
  liftBrand?: string | null;
  liftModelNumber?: string | null;
  liftSerialNumber?: string | null;
  liftCapacityLbs?: number | null;
  liftCapacitySource?: string | null;
  liftTagPhotoUrl?: string | null;
}

// ─── Mappers ──────────────────────────────────────────

const BAY_TYPE_TO_ENTITY: Record<BayTypeEnum, FacilityEntityType> = {
  '2-post': 'lift_2post',
  '4-post': 'lift_4post',
  'scissor': 'lift_scissor',
  'single-post': 'lift_single_post',
  'alignment': 'alignment',
  'adas': 'adas',
  'alignment-adas': 'alignment_adas',
  'wash': 'wash',
  'inspection': 'inspection',
  'general': 'flat',
  'flat': 'flat',
};

/**
 * Default footprint for a persisted `bay_type`, resolved through the same
 * ENTITY_RENDER_CONFIG the Facility Editor uses — the single source of truth
 * for bay-type sizing. Standard types → BAY_WIDTH×BAY_HEIGHT; alignment /
 * ADAS / alignment-ADAS → their specialty envelopes. Used for NEW rows only
 * (e.g. onboarding); persisted geometry is never resized from this.
 */
export function defaultFootprintForBayType(bayType: BayTypeEnum): { width: number; height: number } {
  const cfg = ENTITY_RENDER_CONFIG[BAY_TYPE_TO_ENTITY[bayType] ?? 'flat'];
  return { width: cfg.defaultWidth, height: cfg.defaultHeight };
}

const ENTITY_TO_BAY_TYPE: Record<FacilityEntityType, BayTypeEnum> = {
  lift_2post: '2-post',
  lift_4post: '4-post',
  lift_scissor: 'scissor',
  lift_single_post: 'single-post',
  alignment: 'alignment',
  adas: 'adas',
  alignment_adas: 'alignment-adas',
  wash: 'wash',
  inspection: 'inspection',
  flat: 'flat',
  parking_space: 'general',
  zone: 'general',
  door: 'general',
};

export function mapShopBayToFacilityEntity(bay: ShopBayRow): FacilityEntity {
  const isParking = bay.bay_category === 'parking';
  const entityType: FacilityEntityType = isParking
    ? 'parking_space'
    : (BAY_TYPE_TO_ENTITY[bay.bay_type] ?? 'flat');

  const config = ENTITY_RENDER_CONFIG[entityType];

  return {
    id: bay.id,
    storageSource: 'shop_bay',
    entityType,
    label: bay.name,
    x: bay.position_x,
    y: bay.position_y,
    width: bay.width || config.defaultWidth,
    height: bay.height || config.defaultHeight,
    isOutdoor: bay.is_outdoor ?? false,
    isActive: bay.is_active ?? true,
    isLockedOut: bay.is_locked_out ?? false,
    lockoutReason: bay.lockout_reason,
    isCommunity: bay.is_community ?? false,
    assignedTechId: bay.assigned_tech_id,
    teamId: bay.team_id,
    shopId: bay.shop_id,
    bayNumber: bay.bay_number,
    dbBayType: bay.bay_type,
    dbBayCategory: bay.bay_category,
    liftBrand: (bay as any).lift_brand ?? null,
    liftModelNumber: (bay as any).lift_model_number ?? null,
    liftSerialNumber: (bay as any).lift_serial_number ?? null,
    liftCapacityLbs: (bay as any).lift_capacity_lbs ?? null,
    liftCapacitySource: (bay as any).lift_capacity_source ?? null,
    liftTagPhotoUrl: (bay as any).lift_tag_photo_url ?? null,
  };
}

export function mapFacilityEntityToShopBayUpdate(entity: FacilityEntity): Record<string, any> {
  return {
    id: entity.id,
    name: entity.label,
    position_x: entity.x,
    position_y: entity.y,
    width: entity.width,
    height: entity.height,
    bay_type: ENTITY_TO_BAY_TYPE[entity.entityType] ?? 'general',
    bay_category: entity.entityType === 'parking_space' ? 'parking' : entity.dbBayCategory,
    is_outdoor: entity.isOutdoor,
    is_active: entity.isActive,
    is_locked_out: entity.isLockedOut,
    lockout_reason: entity.lockoutReason,
    is_community: entity.isCommunity,
    assigned_tech_id: entity.assignedTechId,
    team_id: entity.teamId,
    lift_brand: entity.liftBrand,
    lift_model_number: entity.liftModelNumber,
    lift_serial_number: entity.liftSerialNumber,
    lift_capacity_lbs: entity.liftCapacityLbs,
    lift_capacity_source: entity.liftCapacitySource,
    lift_tag_photo_url: entity.liftTagPhotoUrl,
  };
}
